import { useState, useRef, useEffect, useCallback } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { isUs } from '@/lib/dates'
import { addressLabels, formatAddress } from '@/lib/address'

type AddressResult = {
  displayName: string
  addressLine1: string
  city: string
  county: string
  postcode: string
}

type NominatimResult = {
  place_id: number
  display_name: string
  address: {
    house_number?: string
    road?: string
    suburb?: string
    city?: string
    town?: string
    village?: string
    municipality?: string
    county?: string
    state?: string
    state_district?: string
    postcode?: string
    'ISO3166-2-lvl4'?: string
  }
}

function toAddressResult(r: NominatimResult, region?: string | null): AddressResult {
  const a = r.address
  const street = [a.house_number, a.road].filter(Boolean).join(' ')
  const city = a.city ?? a.town ?? a.village ?? a.municipality ?? ''
  // US addresses want the state (two-letter code from ISO 3166-2, e.g. "US-CA"), not Nominatim's `county`.
  const iso = a['ISO3166-2-lvl4']
  const county = isUs(region)
    ? (iso?.startsWith('US-') ? iso.slice(3) : a.state ?? '')
    : a.county ?? a.state_district ?? ''
  return {
    displayName: r.display_name,
    addressLine1: street,
    city,
    county,
    postcode: a.postcode ?? '',
  }
}

export function AddressAutocomplete({
  onSelect,
  region,
}: {
  onSelect: (result: AddressResult) => void
  /** Tenant region (ISO country): restricts the search to that country and sets US/UK wording. */
  region?: string | null
}) {
  const labels = addressLabels(region)
  const countryCode = (region?.trim() || 'GB').toLowerCase()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AddressResult[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 3) { setResults([]); setOpen(false); return }
    setLoading(true)
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=6&countrycodes=${countryCode}`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'CertSlate/1.0' } }
      )
      const data: NominatimResult[] = await res.json()
      setResults(data.map(r => toAddressResult(r, region)))
      setOpen(data.length > 0)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [countryCode, region])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setQuery(v)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(v), 400)
  }

  function handleSelect(r: AddressResult) {
    onSelect(r)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        {loading && <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={labels.search}
          className="w-full h-10 pl-8 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => handleSelect(r)}
                className="w-full text-left px-3 py-2.5 text-sm hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-0"
              >
                <span className="font-medium text-gray-800 block truncate">
                  {formatAddress({ line1: r.addressLine1, city: r.city, county: isUs(region) ? r.county : null, postcode: r.postcode }, region)}
                </span>
                <span className="text-xs text-gray-400 truncate block">{r.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
